import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Fetch_All_Orders } from "../../../hooks/APIHooks";
import { 
  FiArrowLeft, 
  FiPackage, 
  FiTruck, 
  FiCheckCircle, 
  FiUser,
  FiPhone,
  FiMapPin,
  FiDollarSign,
  FiClock,
  FiImage
} from "react-icons/fi";

function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const API_KEY = process.env.REACT_APP_API_KEY;
        const response = await fetch(Fetch_All_Orders, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: API_KEY,
          },
        });
        const result = await response.json();

        if (result.status === "success") {
          const foundOrder = result.data.orders.find(o => o.order_id == orderId);
          if (foundOrder) {
            setOrder(foundOrder);
          } else {
            setError("Order not found");
          }
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError("Failed to fetch order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  const updateDeliveryStatus = async (newStatus) => {
    try {
      const API_KEY = process.env.REACT_APP_API_KEY;
      const response = await fetch(Fetch_All_Orders, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: API_KEY,
        },
        body: JSON.stringify({ order_id: orderId, delivery_status: newStatus }),
      });
      const result = await response.json();

      if (result.status === "success") {
        setOrder(prev => ({ ...prev, delivery_status: newStatus }));
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Failed to update delivery status.");
    }
  };

  const updatePaymentStatus = async (newStatus) => {
    try {
      const API_KEY = process.env.REACT_APP_API_KEY;
      const response = await fetch(Fetch_All_Orders, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: API_KEY,
        },
        body: JSON.stringify({ order_id: orderId, payment_status: newStatus }),
      });
      const result = await response.json();

      if (result.status === "success") {
        setOrder(prev => ({ ...prev, payment_status: newStatus }));
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Failed to update payment status.");
    }
  };

  // Function to get proper image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Handle relative paths
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    const fullUrl = `${baseUrl}/apis/${cleanPath}`;
    console.log('Image URL:', fullUrl);
    return fullUrl;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered": return "text-green-600 bg-green-100";
      case "Processing": return "text-blue-600 bg-blue-100";
      case "Cancelled": return "text-red-600 bg-red-100";
      case "Return": return "text-orange-600 bg-orange-100";
      default: return "text-gray-600 bg-gray-100";
    }
  };

  const getPaymentColor = (status) => {
    switch (status) {
      case "Completed": return "text-green-600 bg-green-100";
      case "Pending": return "text-yellow-600 bg-yellow-100";
      case "Failed": return "text-red-600 bg-red-100";
      default: return "text-gray-600 bg-gray-100";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error || "Order not found"}</p>
          <Link to="/auth/all-orders" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
            ← Back to orders
          </Link>
        </div>
      </div>
    );
  }

  // Safe parse cart items
  const parseCartItems = (cartItems) => {
    try {
      if (!cartItems) return [];
      const parsed = JSON.parse(cartItems);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing cart items:', error);
      return [];
    }
  };

  const cartItems = parseCartItems(order.cart_items);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/auth/all-orders')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <FiArrowLeft />
            Back to orders
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Order #{order.order_id}</h1>
            <p className="text-gray-600">Placed on {new Date(order.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentColor(order.payment_status)}`}>
            {order.payment_status}
          </span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.delivery_status)}`}>
            {order.delivery_status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Order Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiUser className="text-gray-400" />
              Customer Information
            </h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <FiUser className="text-gray-400" />
                <div>
                  <p className="font-medium">{order.full_name}</p>
                  <p className="text-gray-600">{order.email_address}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FiPhone className="text-gray-400" />
                <p className="text-gray-600">{order.phone_number}</p>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiMapPin className="text-gray-400" />
              Shipping Address
            </h2>
            <p className="text-gray-600">{order.shipping_address}</p>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiPackage className="text-gray-400" />
              Order Items ({cartItems.length})
            </h2>
            <div className="space-y-4">
              {cartItems.map((item, index) => {
                const imageUrl = getImageUrl(item.product_image);
                
                return (
                  <div key={index} className="flex items-center gap-4 p-3 border border-gray-100 rounded-lg">
                    <div className="relative">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.product_name}
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-16 h-16 bg-gray-100 rounded flex items-center justify-center ${imageUrl ? 'hidden' : 'flex'}`}
                      >
                        <FiImage className="text-gray-400 text-xl" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.product_name}</p>
                      {item.variant && (
                        <p className="text-sm text-gray-600">Variant: {item.variant}</p>
                      )}
                      <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">₹{item.price}</p>
                      <p className="text-sm text-gray-600">₹{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column - Actions and Summary */}
        <div className="space-y-6">
          {/* Order Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiDollarSign className="text-gray-400" />
              Order Summary
            </h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>₹{order.subtotal}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span>₹{order.shipping_charge}</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span>₹{order.total_amount}</span>
              </div>
            </div>
          </div>

          {/* Status Controls */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FiTruck className="text-gray-400" />
              Update Status
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Status
                </label>
                <select
                  value={order.payment_status}
                  onChange={(e) => updatePaymentStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Status
                </label>
                <select
                  value={order.delivery_status}
                  onChange={(e) => updateDeliveryStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Processing">Processing</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Return">Return</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          {order.razorpay_payment_id && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FiCheckCircle className="text-gray-400" />
                Payment Information
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment ID:</span>
                  <span className="font-mono">{order.razorpay_payment_id}</span>
                </div>
                {order.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-mono">{order.transaction_id}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}

export default OrderDetails;